import { IBankAccount, SalesTaxFiling } from '@commenda/api';
import { Icons } from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { formatAccountType } from '../../../bankAccount/utils';
import { Filing } from '../../../molecules';
import { formatNumberWithCommasAndDecimals } from '../../../sales-tax/utils';
import {
  FilingBreakdownAccordion,
  FilingBreakdownProps,
} from './FilingBreakdownAccordion';

interface FiledAmountCardProps {
  filing: Filing<SalesTaxFiling>;
  isFiled: boolean;
  bankAccount: IBankAccount;
  onViewTransactions?: () => void;
}

export const FiledAmountCard = ({
  filing,
  isFiled,
  bankAccount,
  onViewTransactions,
}: FiledAmountCardProps) => {
  const resolvedAccountNumber = bankAccount?.credentials?.accountNumber ?? '';

  // Function to format account number to show last 4 digits
  const formatAccountNumber = (number: string) => {
    const last4 = number.slice(-4);
    return `•••• •••• ${last4}`;
  };

  const breakdown: FilingBreakdownProps['breakdown'] = {
    taxPaid: isFiled
      ? (filing?.metadata?.tax_paid ?? 0)
      : (filing?.metadata?.estimated_tax_collected ?? 0),
    taxDue: isFiled
      ? (filing?.metadata?.tax_paid ?? 0)
      : (filing?.metadata?.estimated_tax_collected ?? 0),
    tax_liability: isFiled
      ? (filing?.metadata?.tax_liability ?? 0)
      : (filing?.metadata?.estimated_tax_collected ?? 0),
    filingDate: filing?.filedAt,
    taxableSales: isFiled
      ? (filing?.metadata?.taxable_amount ?? 0)
      : (filing?.metadata?.estimated_taxable_amount ?? 0),
    nonTaxableSales: isFiled
      ? (filing?.metadata?.non_taxable_amount ?? 0)
      : (filing?.metadata?.estimated_non_taxable_amount ?? 0),

    marketplaceSales: isFiled
      ? (filing?.metadata?.marketplace_amount ?? 0)
      : (filing?.metadata?.estimated_marketplace_amount ?? 0),

    exemptSales: isFiled
      ? (filing?.metadata?.exempt_amount ?? 0)
      : (filing?.metadata?.estimated_exempt_amount ?? 0),

    grossSales: isFiled
      ? (filing?.metadata?.gross_amount ?? 0)
      : (filing?.metadata?.estimated_gross_amount ?? 0),

    numberOfTransactions: filing?.metadata?.filing_transactions_count ?? 0,
    adjustments: {
      prepayment: filing?.metadata?.prepayment ?? 0,
      priorPayment: filing?.metadata?.prior_payment ?? 0,
      retailDeliveryFee: filing?.metadata?.retail_delivery_fee ?? 0,
      jurisdictionCredit: filing?.metadata?.jurisdiction_issued_credit ?? 0,
      vendorDiscount: filing?.metadata?.vendor_discount ?? 0,
      rounding: filing?.metadata?.rounding ?? 0,
      penalty: filing?.metadata?.penalty ?? 0,
      interest: filing?.metadata?.interest ?? 0,
    },
  };

  const formatDateString = (dateString: string): string => {
    if (!dateString) return '';
    const date = NonTimezoneSensitiveDate.fromISO8601String(dateString);
    return date.format('MMM D, YYYY');
  };

  return (
    <div className="rounded-2xl border border-border-primary bg-white flex max-h-[309px]">
      <div className="flex-1 p-3xl ">
        <div className="flex flex-col gap-4xl">
          <div className="flex flex-col gap-md">
            <div className="flex items-center gap-xs">
              <span className="text-text-quaternary txt-small-medium">
                {isFiled ? 'Tax paid' : 'Estimated tax due'}
              </span>
            </div>

            <div className="flex items-center gap-md">
              <div className="flex items-center gap-sm">
                <span className="text-text-quaternary display-sm-regular">
                  $
                </span>
                <span className="text-text-primary display-md-bold">
                  {isFiled
                    ? formatNumberWithCommasAndDecimals(
                        filing?.metadata?.tax_paid ?? 0
                      )
                    : formatNumberWithCommasAndDecimals(
                        filing?.metadata?.estimated_tax_collected ?? 0
                      )}
                </span>
              </div>
              <div
                onClick={onViewTransactions}
                className="flex items-center gap-sm text-utility-blue-600 txt-small-medium cursor-pointer border-b border-utility-blue-600 hover:text-utility-blue-700"
              >
                View transactions
                <Icons.ArrowUpRight className="size-4" />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-md">
            <span className="text-text-quaternary txt-small-medium">
              Filed on
            </span>

            <div className="text-text-primary txt-small-medium">
              {isFiled
                ? filing?.filedAt
                  ? formatDateString(filing?.filedAt)
                  : 'Filing date not available'
                : 'Not filed'}
            </div>
          </div>

          {bankAccount?.bankName && (
            <div className="flex flex-col gap-md">
              <span className="text-text-quaternary txt-small-medium">
                Remittance bank account
              </span>

              <div className="flex items-center gap-xl">
                <div className="flex items-center gap-md">
                  <div className="size-12 rounded-lg border border-border-primary bg-white flex items-center justify-center">
                    <Icons.Building02 className="size-6 text-text-quaternary" />
                  </div>

                  <div className="flex flex-col gap-xs">
                    <span className="text-text-primary txt-small-medium">
                      {bankAccount.bankName}
                    </span>
                    <div className="flex items-center gap-sm">
                      {bankAccount.subType && (
                        <span className="text-text-secondary txt-small-regular pr-2 border-r border-border-primary">
                          {formatAccountType(bankAccount.subType)}
                        </span>
                      )}

                      <span className="text-text-secondary txt-small-bold">
                        {formatAccountNumber(resolvedAccountNumber)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className={`flex flex-col gap-3xl p-3xl rounded-xl m-2 flex-1 overflow-y-scroll ${
          isFiled ? 'bg-bg-success' : 'bg-bg-secondary'
        }`}
      >
        <FilingBreakdownAccordion breakdown={breakdown} />
      </div>
    </div>
  );
};
